import React, { useState } from 'react';
import { Input, Button, Modal, Spin, InputNumber, Tag, Descriptions, Table } from 'antd';
import { useNewProductReportForm } from './hooks/useNewProductReportForm';
import { activeChildFamilyKeys } from '../../services/newProductReportApi';
import { newProductReportApi } from '../../services/newProductReportApi';
import { BatchAcceptanceBridge, pwj228f1AcceptanceManifest } from '../../features/acceptance';

const { TextArea } = Input;

const J228Form: React.FC = () => {
  const {
    mode, isEditing, isApproved, loading,
    records, current, currentIdx,
    formValues, updateField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, doApprove, quickSearch,
  } = useNewProductReportForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${current.fj780_01}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleApprove = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准「${current.fj780_01}」？核准後不可修改或刪除。`,
      okText: '核准',
      cancelText: '取消',
      onOk: doApprove,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  // Computed cm values (auto-convert from inch per spec section 8)
  const cmWidth = Math.round(formValues.fj780_19 * 2.54 * 10) / 10;
  const cmFullWidth = Math.round(formValues.fj780_191 * 2.54 * 10) / 10;

  // Grid columns for test items (FJ781)
  const testItemColumns = [
    { title: <span className="field-label" data-contract-field="fj781_02">項次</span>, dataIndex: 'fj781_02', key: 'fj781_02', width: 60, render: (_: unknown, __: unknown, idx: number) => idx + 1 },
    { title: <span className="field-label" data-contract-field="fj781_10">項目</span>, dataIndex: 'fj781_10', key: 'fj781_10', width: 120 },
    { title: <span className="field-label" data-contract-field="fj781_11">規範</span>, dataIndex: 'fj781_11', key: 'fj781_11', width: 120 },
    { title: <span className="field-label" data-contract-field="fj781_12">承諾數據</span>, dataIndex: 'fj781_12', key: 'fj781_12', width: 120 },
    { title: <span className="field-label" data-contract-field="fj781_13">允差%</span>, dataIndex: 'fj781_13', key: 'fj781_13', width: 180 },
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          新產品開發分析表 (pwj228f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
          {current && (
            <span className="field-label ml-2" data-contract-field="fj780_80">
              <Tag color={isApproved ? 'green' : 'default'}>
                核准狀態：{isApproved ? '已核准' : '未核准'}
              </Tag>
            </span>
          )}
        </h2>

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入文件編號或產品代號查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 300 }}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Main Form */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-6">
          {/* Row 0 - Document IDs (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">分析單號 (FJ780_01)</label>
            <Input value={current?.fj780_01 || ''} disabled data-contract-field="fj780_01" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開發申請單 (FJ780_02)</label>
            <Input value={formValues.fj780_02} onChange={e => updateField('fj780_02', e.target.value)}
              disabled={!isEditing} maxLength={5} data-contract-field="fj780_02" />
          </div>

          {/* Row 1 - Date and Total Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">建檔日期 (FJ780_03)</label>
            <Input value={current?.fj780_03 || ''} disabled data-contract-field="fj780_03" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">總項數 (FJ780_30)</label>
            <InputNumber value={current?.fj780_30 || 0} disabled style={{ width: '100%' }} data-contract-field="fj780_30" />
          </div>

          {/* Row 2 - Product Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">產品代號前段 (FJ780_10)*</label>
            <Input value={formValues.fj780_10} onChange={e => updateField('fj780_10', e.target.value)}
              disabled={!isEditing} maxLength={6} data-contract-field="fj780_10" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">產品代號後段 (FJ780_11)</label>
            <Input value={formValues.fj780_11} onChange={e => updateField('fj780_11', e.target.value)}
              disabled={!isEditing} maxLength={6} data-contract-field="fj780_11" />
          </div>

          {/* Row 3 - Product Description (editbox -> TextArea) */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">產品說明 (FJ780_12)</label>
            <TextArea value={formValues.fj780_12} onChange={e => updateField('fj780_12', e.target.value)}
              disabled={!isEditing} rows={3} data-contract-field="fj780_12" />
          </div>

          {/* Row 4 - Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">色號 (FJ780_13)*</label>
            <Input value={formValues.fj780_13} onChange={e => updateField('fj780_13', e.target.value)}
              disabled={!isEditing} maxLength={3} data-contract-field="fj780_13" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">色織版號 (FJ780_14)</label>
            <Input value={formValues.fj780_14} onChange={e => updateField('fj780_14', e.target.value)}
              disabled={!isEditing} data-contract-field="fj780_14" />
          </div>

          {/* Row 5 - Quantity & Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開發數量 (FJ780_15)*</label>
            <InputNumber value={formValues.fj780_15}
              onChange={v => updateField('fj780_15', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={2} data-contract-field="fj780_15" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">數量單位 (FJ780_16)</label>
            <Input value={formValues.fj780_16} onChange={e => updateField('fj780_16', e.target.value)}
              disabled={!isEditing} maxLength={3} data-contract-field="fj780_16" />
          </div>

          {/* Row 6 - Cost & Cost Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">單位成本 (FJ780_17)*</label>
            <InputNumber value={formValues.fj780_17}
              onChange={v => updateField('fj780_17', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={3} data-contract-field="fj780_17" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">成本幣別 (FJ780_171)</label>
            <Input value={formValues.fj780_171} onChange={e => updateField('fj780_171', e.target.value)}
              disabled={!isEditing} maxLength={3} data-contract-field="fj780_171" />
          </div>

          {/* Row 7 - Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">幣別 (FJ780_18)</label>
            <Input value={formValues.fj780_18} onChange={e => updateField('fj780_18', e.target.value)}
              disabled={!isEditing} maxLength={3} placeholder="NTD" data-contract-field="fj780_18" />
          </div>
          <div />

          {/* Row 8 - Width inch/cm */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">幅寬(可用) inch (FJ780_19)</label>
            <InputNumber value={formValues.fj780_19}
              onChange={v => updateField('fj780_19', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={1} data-contract-field="fj780_19" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">幅寬(可用) cm (自動換算)</label>
            <InputNumber value={cmWidth} disabled style={{ width: '100%' }} precision={1} data-contract-field="fj780_20" />
          </div>

          {/* Row 9 - Full Width inch/cm */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">幅寬(全幅) inch (FJ780_191)</label>
            <InputNumber value={formValues.fj780_191}
              onChange={v => updateField('fj780_191', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={1} data-contract-field="fj780_191" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">幅寬(全幅) cm (自動換算)</label>
            <InputNumber value={cmFullWidth} disabled style={{ width: '100%' }} precision={1} data-contract-field="fj780_201" />
          </div>

          {/* Row 10 - Tolerance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">允差% inch (FJ780_192)</label>
            <InputNumber value={formValues.fj780_192}
              onChange={v => updateField('fj780_192', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={2} data-contract-field="fj780_192" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">允差% cm (自動換算)</label>
            <InputNumber
              value={isEditing ? formValues.fj780_192 : (current?.fj780_202 ?? formValues.fj780_192)}
              disabled
              style={{ width: '100%' }}
              precision={2}
              data-contract-field="fj780_202"
            />
          </div>

          {/* Row 11 - Weight g/sqm */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">克重 g/sqm (FJ780_21)</label>
            <InputNumber value={formValues.fj780_21}
              onChange={v => updateField('fj780_21', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={1} data-contract-field="fj780_21" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">克重 g/sqm 允差 (FJ780_211)</label>
            <InputNumber value={formValues.fj780_211}
              onChange={v => updateField('fj780_211', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={2} data-contract-field="fj780_211" />
          </div>

          {/* Row 12 - Weight g/yd */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">碼重 g/yd (FJ780_22)</label>
            <InputNumber value={formValues.fj780_22}
              onChange={v => updateField('fj780_22', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={1} data-contract-field="fj780_22" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">碼重 g/yd 允差 (FJ780_221)</label>
            <InputNumber value={formValues.fj780_221}
              onChange={v => updateField('fj780_221', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={2} data-contract-field="fj780_221" />
          </div>

          {/* Row 13 - Density */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">經向密度 T/INCH (FJ780_23)</label>
            <InputNumber value={formValues.fj780_23}
              onChange={v => updateField('fj780_23', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={1} data-contract-field="fj780_23" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">緯向密度 T/INCH (FJ780_24)</label>
            <InputNumber value={formValues.fj780_24}
              onChange={v => updateField('fj780_24', v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} min={0} precision={1} data-contract-field="fj780_24" />
          </div>
        </div>

        {/* Audit info */}
        {current && mode === 'browse' && (
          <Descriptions size="small" bordered className="mb-4" column={2}>
            <Descriptions.Item label={<span className="field-label" data-contract-field="fj780_91">建立人員</span>}>{current.fj780_91}</Descriptions.Item>
            <Descriptions.Item label={<span className="field-label" data-contract-field="fj780_92">建立日期</span>}>{current.fj780_92 || '-'}</Descriptions.Item>
            <Descriptions.Item label={<span className="field-label" data-contract-field="fj780_93">最後異動</span>}>{current.fj780_93}</Descriptions.Item>
          </Descriptions>
        )}

        {/* Test Items Grid (FJ781) - 5 columns per field_contract */}
        {current && current.test_items && current.test_items.length > 0 && mode === 'browse' && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              測試項目 ({current.test_items.length} 筆)
            </h3>
            <Table
              dataSource={current.test_items}
              columns={testItemColumns}
              rowKey={(_, idx) => String(idx)}
              size="small"
              pagination={false}
              scroll={{ y: 200 }}
            />
          </div>
        )}

        {current && mode === 'browse' && (
          <div data-acceptance-non-field="diagnostic-child-family-counts">
            <Descriptions title="Active child-family rows" size="small" bordered className="mb-4" column={3}>
              {activeChildFamilyKeys.map((key) => (
                <Descriptions.Item key={key} label={key}>
                  {current[key]?.length || 0}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current || isApproved}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current || isApproved}>刪除</Button>
              <Button onClick={handleApprove} disabled={!current || isApproved}>核准</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
        <BatchAcceptanceBridge
          manifest={pwj228f1AcceptanceManifest}
          records={records}
          selectedKeys={current?.fj780_01 ? [current.fj780_01] : []}
          keyOf={(item) => item.fj780_01}
          queryExact={(key) => newProductReportApi.get(key)}
          lastApiResponse={current}
          loading={loading}
          onAutoFill={navFirst}
          autoFillDisabled={records.length === 0}
          screenDraft={formValues}
        />
      </div>
    </Spin>
  );
};

export default J228Form;
